import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api-client';
import { ApiError } from '@sw/api-client';
import { AUTH_ME_QUERY_KEY } from '@sw/auth';
import { useAuth } from '../../lib/auth';
import { tokens } from '../../theme/tokens';
import { LocaleSwitcher } from '../../components/LocaleSwitcher';
import { TenantBrand } from '../../tenant/TenantBrand';

/**
 * Magic-link landing. The URL emitted by email is:
 *   {frontendBase}/auth/magic-link/verify#token={guid}&accessLinkToken={guid}
 *
 * The token travels in the FRAGMENT (#), not the query string — the server never
 * receives it in the initial GET, and fragments are absent from Referer headers,
 * proxy/CDN logs, and browser history. We clear the fragment via replaceState as soon
 * as it's read.
 *
 * If the user is already authenticated in another tab, we skip the verify POST and
 * navigate straight to /opportunities. Success shows a brief "You're in" before
 * navigate (SUCCESS_DISPLAY_MS) so the transition reads as deliberate, not a flash.
 */
type VerifyState =
  | { status: 'loading' }
  | { status: 'success' }
  | { status: 'error'; message: string };

const SUCCESS_DISPLAY_MS = 800;

function parseHashParams(hash: string): URLSearchParams {
  const clean = hash.startsWith('#') ? hash.slice(1) : hash;
  return new URLSearchParams(clean);
}

const VERIFY_ERROR_CODE_KEYS: Record<string, string> = {
  token_expired: 'auth.verify.errors.tokenExpired',
  token_already_used: 'auth.verify.errors.tokenAlreadyUsed',
  token_not_found: 'auth.verify.errors.tokenNotFound',
};

function mapVerifyError(
  code: string | false | undefined,
  t: (key: string) => string,
  fallback: string,
): string {
  if (typeof code !== 'string') return fallback;
  const key = VERIFY_ERROR_CODE_KEYS[code];
  return key ? t(key) : fallback;
}

export default function MagicLinkVerifyPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [state, setState] = useState<VerifyState>({ status: 'loading' });
  const navigatedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      // Edge case: user ya autenticado (otra tab, p.ej.) → skip verify.
      if (auth.isAuthenticated && !navigatedRef.current) {
        navigatedRef.current = true;
        navigate('/opportunities', { replace: true });
        return;
      }
      if (auth.isLoading) return;

      const params = parseHashParams(window.location.hash);
      const token = params.get('token');
      const accessLinkToken = params.get('accessLinkToken');

      if (!token || !accessLinkToken) {
        if (!cancelled) setState({ status: 'error', message: t('auth.verify.malformed') });
        return;
      }

      // Limpiar fragment inmediatamente — token sigue en memoria pero ya no en address bar.
      window.history.replaceState(null, '', window.location.pathname);

      try {
        await api.post('/api/client/auth/magic-link/verify', {
          token,
          accessLinkToken,
        });
        if (cancelled) return;
        // Monorepo equiv de auth.refresh() — invalidate forza un re-fetch del /me
        // y `auth.isAuthenticated` se actualiza en el próximo render.
        await queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
        if (cancelled) return;
        setState({ status: 'success' });
        setTimeout(() => {
          if (!navigatedRef.current) {
            navigatedRef.current = true;
            navigate('/opportunities', { replace: true });
          }
        }, SUCCESS_DISPLAY_MS);
      } catch (err) {
        if (cancelled) return;
        const fallback = t('auth.verify.invalid');
        // Backend error codes are mapped to local i18n keys; raw server strings are
        // never rendered (defends against future regressions surfacing internal details
        // and removes the XSS vector if the rendering surface ever adopts HTML).
        const code =
          err instanceof ApiError
            ? (err.body as { error?: string } | null | undefined)?.error
            : undefined;
        const message = mapVerifyError(code, t, fallback);
        setState({ status: 'error', message });
      }
    }

    void run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated, auth.isLoading]);

  return (
    <Box
      data-testid="verify-page"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: tokens.color.bg,
        color: tokens.color.ink,
        fontFamily: tokens.font.display,
        lineHeight: 1.5,
        WebkitFontSmoothing: 'antialiased',
        position: 'relative',
      }}
    >
      <BackgroundGrid />
      <TopStrip />
      <Box
        component="main"
        sx={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2, sm: 4 },
          py: { xs: 3, sm: 4 },
        }}
      >
        <Box
          data-testid="verify-card"
          sx={{
            width: '100%',
            maxWidth: 440,
            background: tokens.color.surface,
            border: `1px solid ${tokens.color.line}`,
            borderRadius: '18px',
            p: { xs: '32px 22px', sm: '40px 36px' },
            boxShadow:
              '0 1px 0 rgba(15,15,15,.04), 0 30px 60px -30px rgba(15,15,15,.18)',
            textAlign: 'center',
          }}
        >
          <Box
            data-testid="verify-tenant-brand"
            sx={{
              pb: '22px',
              borderBottom: `1px solid ${tokens.color.lineSoft}`,
              mb: '26px',
              textAlign: 'left',
            }}
          >
            <TenantBrand size="card" />
          </Box>

          {state.status === 'loading' && (
            <Box
              data-testid="verify-loading"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                alignItems: 'center',
                py: 2,
              }}
            >
              <CircularProgress size={28} />
              <Typography sx={{
                color: "text.secondary"
              }}>{t('auth.verify.loading')}</Typography>
            </Box>
          )}

          {state.status === 'success' && (
            <Box data-testid="verify-success" sx={{ py: 1 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  mx: 'auto',
                  mb: '18px',
                  borderRadius: '16px',
                  background: tokens.color.posSoft,
                  color: tokens.color.pos,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.4}
                  width="26"
                  height="26"
                >
                  <path d="M5 12.5l4.5 4.5L19 7.5" />
                </svg>
              </Box>
              <Typography variant="h2" gutterBottom>
                {t('auth.verify.success')}
              </Typography>
              <Typography sx={{
                color: "text.secondary"
              }}>{t('auth.verify.redirecting')}</Typography>
            </Box>
          )}

          {state.status === 'error' && (
            <Alert severity="warning" data-testid="verify-error" sx={{ textAlign: 'left' }}>
              {state.message}
            </Alert>
          )}
        </Box>
      </Box>
      <SecureFooter />
    </Box>
  );
}

function BackgroundGrid() {
  return (
    <Box
      aria-hidden
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        backgroundImage: `
          radial-gradient(circle at 20% 0%, rgba(15,118,110,.06), transparent 40%),
          radial-gradient(circle at 100% 100%, rgba(31,111,235,.05), transparent 50%),
          linear-gradient(transparent 95%, rgba(10,10,10,.05) 95%),
          linear-gradient(90deg, transparent 95%, rgba(10,10,10,.05) 95%)
        `,
        backgroundSize: 'auto, auto, 28px 28px, 28px 28px',
      }}
    />
  );
}

function TopStrip() {
  return (
    <Box
      sx={{
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: { xs: '20px', sm: '40px' },
        py: '18px',
      }}
    >
      <Box
        sx={{
          fontFamily: tokens.font.mono,
          fontSize: 10.5,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: tokens.color.muted2,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: '5px',
            background: tokens.color.ink,
            color: '#fff',
            display: 'grid',
            placeItems: 'center',
            fontSize: 9.5,
            fontWeight: 700,
          }}
        >
          S
        </Box>
        Client Portal · Powered by Scout
      </Box>
      <LocaleSwitcher variant="topstrip" />
    </Box>
  );
}

function SecureFooter() {
  return (
    <Box
      component="footer"
      sx={{
        position: 'relative',
        zIndex: 1,
        px: { xs: '20px', sm: '40px' },
        py: '20px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '14px',
        justifyContent: 'space-between',
        fontFamily: tokens.font.mono,
        fontSize: 10.5,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: tokens.color.muted2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>SOC 2 · TLS 1.3</Box>
    </Box>
  );
}
